import Yup from 'yup';

import {
  numberType,
  requiredNumberType,
  stringType,
  requiredStringType,
  requiredAutocompleteType,
  requiredTextType,
  requiredEmailType,
  urlType,
  requiredDateType
} from './validationTypes';

const { indices } = require('buyside/util/constantsFromRails.json');

const executiveType = Yup.object().shape({
  name: requiredStringType,
  bio: requiredTextType
});

const firmReferenceType = Yup.object().shape({
  name: requiredStringType,
  title: requiredStringType,
  employer: requiredStringType,
  contactEmail: requiredEmailType,
  relationshipToFund: requiredStringType
});

const supportingDocumentType = Yup.object().shape({
  name: requiredStringType,
  filename: requiredStringType,
  description: requiredTextType
});

const returnType = Yup.object().shape({
  employerBenchmark: Yup.boolean().nullable(),
  benchmark: Yup.string().when('employerBenchmark', {
    is: false,
    then: Yup.string().oneOf(indices, 'Please select a benchmark.'),
    otherwise: requiredStringType
  }),
  ytd: numberType,
  ltm: numberType,
  oneYear: numberType,
  threeYear: numberType,
  fiveYear: numberType,
  sevenYear: numberType,
  tenYear: numberType,
  sinceInception: numberType
});

const holdingType = Yup.object().shape({
  primarySymbol: requiredAutocompleteType,
  percentOfPortfolio: requiredNumberType,
  averageCost: requiredNumberType,
  priceTarget: requiredNumberType,
  timeframe: requiredStringType
});

const marketingMaterialType = Yup.object().shape({
  name: requiredStringType,
  filename: stringType.test({
    test: function(filename) {
      return filename || this.parent.url;
    },
    message: 'Either URL or file is required.'
  }),
  category: requiredStringType,
  url: urlType.test({
    test: function(url) {
      return url || this.parent.filename;
    },
    message: 'Either URL or file is required.'
  })
});

const pressType = Yup.object().shape({
  title: requiredStringType,
  url: urlType.test({
    test: function(url) {
      return url || this.parent.filename;
    },
    message: 'Either URL or file is required.'
  }),
  filename: stringType.test({
    test: function(filename) {
      return filename || this.parent.url;
    },
    message: 'Either URL or file is required.'
  }),
  publishDate: requiredDateType,
  description: requiredTextType
});

const dueDiligenceDocumentType = Yup.object().shape({
  name: requiredStringType,
  filename: requiredStringType,
  category: requiredStringType,
  description: requiredStringType
});

const serviceProviderType = Yup.object().shape({
  category: requiredStringType,
  firmName: requiredStringType,
  contactName: requiredStringType,
  contactEmail: requiredEmailType,
  contactPhoneNumber: requiredStringType
});

export const cardSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    aum: requiredNumberType,
    foundingDate: requiredDateType,
    fundType: requiredStringType,
    strategy: requiredStringType,
    focus: stringType,
    location: requiredAutocompleteType,
    fundManager: requiredStringType,
    description: requiredTextType.max(1250, 'Description cannot be greater than 1,250 characters')
  })
});

export const executiveManagementSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    executives: Yup.array().of(executiveType)
  })
});

export const firmReferencesSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    firmReferences: Yup.array().of(firmReferenceType)
  })
});

export const expertiseSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    subsectorExpertise: stringType
  })
});

export const returnsSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    returns: Yup.array().of(returnType),
    returnsUpdatedDate: requiredDateType,
    returnsDocuments: Yup.array().of(supportingDocumentType)
  })
});

export const fundHoldingsSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    holdings: Yup.array().of(holdingType),
    holdingsDocuments: Yup.array().of(supportingDocumentType)
  })
});

export const marketingMaterialsSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    marketingMaterials: Yup.array().of(marketingMaterialType)
  })
});

export const pressMediaSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    presses: Yup.array().of(pressType)
  })
});

export const metricsSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    sharpeRatio: numberType,
    beta: numberType,
    turnoverRatio: numberType,
    metricsDocuments: Yup.array().of(supportingDocumentType)
  })
});

export const feeStructureSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    managementFee: numberType,
    incentiveFee: numberType,
    hurdleRate: numberType,
    lockUp: numberType,
    feeStructureDocuments: Yup.array().of(supportingDocumentType)
  })
});

export const dueDiligenceSchema = Yup.object().shape({
  fundProfile: Yup.object().shape({
    dueDiligenceDocuments: Yup.array().of(dueDiligenceDocumentType),
    serviceProviders: Yup.array().of(serviceProviderType)
  })
});
